/**
 * KebabMenu — bouton ⋮ + dropdown d'actions contextuelles.
 *
 * Usage :
 *   <KebabMenu
 *     ariaLabel="Actions sur Sillage"
 *     items={[
 *       { id: 'edit', label: 'Modifier', icon: Pencil, onSelect: () => navigate(`/works/${id}/edit`) },
 *       { id: 'duplicate', label: 'Dupliquer', icon: Copy, onSelect: handleDuplicate },
 *       { id: 'archive', label: 'Archiver', icon: Archive, onSelect: handleArchive },
 *       { id: 'delete', label: 'Supprimer', icon: Trash2, variant: 'danger', onSelect: handleDelete },
 *     ]}
 *   />
 *
 * Comportements :
 * - Clic sur le bouton bascule l'ouverture.
 * - Clic outside / Escape ferme.
 * - Flèches ↑/↓ naviguent, Enter sélectionne, Esc ferme.
 * - L'événement de clic sur le bouton fait `stopPropagation()` pour permettre
 *   l'usage à l'intérieur d'un `<Link>` sans déclencher la navigation.
 *
 * Le menu se positionne par défaut sous le bouton, aligné à droite. Pour les
 * cas où on veut le caler ailleurs, le composant respecte l'ancrage que le
 * parent fournit via `align="left"` ou `placement="top"`.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'

import './KebabMenu.css'

export interface KebabMenuItem {
  /** Clé stable — utilisée pour key React + aria. */
  id: string
  label: string
  /** Icône lucide (ou tout composant équivalent). 14×14 par convention. */
  icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' | 'false' }>
  /** Action déclenchée à la sélection. Le menu se ferme automatiquement après. */
  onSelect: () => void
  /** Style 'danger' = rouge sang (Supprimer). */
  variant?: 'default' | 'danger'
  /** Désactive l'item (gris + non cliquable). */
  disabled?: boolean
}

export interface KebabMenuProps {
  items: KebabMenuItem[]
  /** Aria-label obligatoire — précise l'objet sur lequel agissent les actions. */
  ariaLabel: string
  /** Alignement horizontal du menu par rapport au bouton. Défaut : 'right'. */
  align?: 'left' | 'right'
  /** Placement vertical. Défaut : 'bottom'. */
  placement?: 'top' | 'bottom'
  /** Taille du bouton ⋮. Défaut : 'sm'. */
  size?: 'sm' | 'md'
  /** Variante visuelle du bouton (ex: 'overlay' pour cartes). Défaut : 'plain'. */
  buttonVariant?: 'plain' | 'overlay'
  className?: string
}

interface MenuPosition {
  top: number
  left: number
  /** Largeur du trigger — sert à aligner la liste précisément. */
  triggerWidth: number
}

export default function KebabMenu({
  items,
  ariaLabel,
  align = 'right',
  placement = 'bottom',
  size = 'sm',
  buttonVariant = 'plain',
  className,
}: KebabMenuProps) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const menuId = useId()

  // Calcule la position du dropdown en coordonnées viewport. Le menu est
  // rendu via portal (échappe les `overflow: hidden` parents) et positionné
  // en `fixed` — la position se recalcule à chaque ouverture, scroll ou
  // resize tant que la modal est ouverte.
  const computePosition = (): MenuPosition | null => {
    const trigger = buttonRef.current
    if (!trigger) return null
    const rect = trigger.getBoundingClientRect()
    return {
      top: placement === 'bottom' ? rect.bottom : rect.top,
      left: align === 'right' ? rect.right : rect.left,
      triggerWidth: rect.width,
    }
  }

  // Mesure DOM-driven d'un trigger fraîchement monté → setState dans
  // useLayoutEffect est le pattern standard d'un popover (mesurer-
  // puis-positionner). Pas de boucle — la valeur stabilise en 1 frame.
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null)
      return
    }
    setMenuPosition(computePosition())
  }, [open])
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!open) return
    const reposition = () => {
      const next = computePosition()
      if (next) setMenuPosition(next)
    }
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      const inContainer = containerRef.current?.contains(target) ?? false
      const inList = listRef.current?.contains(target) ?? false
      if (!inContainer && !inList) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const enabledIndices = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0)

  const handleButtonClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen((v) => !v)
    setActiveIndex(enabledIndices[0] ?? 0)
  }

  const handleButtonKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      setOpen(true)
      setActiveIndex(enabledIndices[0] ?? 0)
    }
  }

  const selectIndex = (index: number) => {
    const item = items[index]
    if (!item || item.disabled) return
    setOpen(false)
    item.onSelect()
  }

  const handleListKey = (event: ReactKeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      const cur = enabledIndices.indexOf(activeIndex)
      const next = enabledIndices[(cur + 1) % enabledIndices.length] ?? activeIndex
      setActiveIndex(next)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      const cur = enabledIndices.indexOf(activeIndex)
      const prev =
        enabledIndices[(cur - 1 + enabledIndices.length) % enabledIndices.length] ?? activeIndex
      setActiveIndex(prev)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectIndex(activeIndex)
    }
  }

  const containerClass = [
    'kebab-menu',
    `kebab-menu--align-${align}`,
    `kebab-menu--placement-${placement}`,
    `kebab-menu--button-${buttonVariant}`,
    open ? 'kebab-menu--open' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  // Le menu lui-même est rendu via portal pour échapper aux ancêtres en
  // `overflow: hidden` (cas typique : `.settings-table-wrapper` arrondi).
  // Style inline en `position: fixed` + coordinates viewport calculées.
  const listStyle: React.CSSProperties | undefined = menuPosition
    ? {
        position: 'fixed',
        top: placement === 'bottom' ? menuPosition.top + 4 : 'auto',
        bottom: placement === 'top' ? `calc(100vh - ${menuPosition.top}px + 4px)` : 'auto',
        left: align === 'left' ? menuPosition.left : 'auto',
        right: align === 'right' ? `calc(100vw - ${menuPosition.left}px)` : 'auto',
        margin: 0,
      }
    : undefined

  const menuList =
    open && menuPosition ? (
      <ul
        id={menuId}
        ref={listRef}
        role="menu"
        className="kebab-menu-list kebab-menu-list--portal"
        tabIndex={-1}
        onKeyDown={handleListKey}
        aria-label={ariaLabel}
        style={listStyle}
      >
        {items.map((item, index) => {
          const Icon = item.icon
          const itemClass = [
            'kebab-menu-item',
            item.variant === 'danger' ? 'kebab-menu-item--danger' : '',
            item.disabled ? 'kebab-menu-item--disabled' : '',
            index === activeIndex ? 'kebab-menu-item--active' : '',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li key={item.id} role="none">
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                disabled={item.disabled}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  selectIndex(index)
                }}
              >
                {Icon && <Icon size={14} aria-hidden="true" />}
                <span>{item.label}</span>
              </button>
            </li>
          )
        })}
      </ul>
    ) : null

  return (
    <div className={containerClass} ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`kebab-menu-trigger kebab-menu-trigger--${size}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        onClick={handleButtonClick}
        onKeyDown={handleButtonKey}
      >
        <MoreVertical size={size === 'sm' ? 14 : 16} aria-hidden="true" />
      </button>
      {menuList && createPortal(menuList, document.body)}
    </div>
  )
}
